import React, { useState, useRef } from "react";
import styles from "./Cards.module.scss";
import Card from "./Card";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const CardsSwiper = ({ cards }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollContainerRef = useRef(null);

  // Virtualization: only render cards within this range
  const BUFFER_SIZE = 3; // Number of cards to render before/after visible card

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const scrollLeft = container.scrollLeft;
    const cardWidth = container.scrollWidth / cards.length;
    const newIndex = Math.round(scrollLeft / cardWidth);

    if (newIndex !== activeIndex && newIndex >= 0 && newIndex < cards.length) {
      setActiveIndex(newIndex);
    }
  };

  // Calculate visible range
  const startIndex = Math.max(0, activeIndex - BUFFER_SIZE);
  const endIndex = Math.min(cards.length - 1, activeIndex + BUFFER_SIZE);

  // Check if a card should be rendered
  const shouldRenderCard = (index) => {
    return index >= startIndex && index <= endIndex;
  };

  const scrollToCard = (index) => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const cardWidth = container.scrollWidth / cards.length;
    container.scrollTo({
      left: cardWidth * index,
      behavior: "smooth",
    });
  };

  const handlePrevious = () => {
    if (activeIndex > 0) {
      scrollToCard(activeIndex - 1);
    }
  };

  const handleNext = () => {
    if (activeIndex < cards.length - 1) {
      scrollToCard(activeIndex + 1);
    }
  };

  return (
    <>
      {/* Pagination dots */}
      <div className={styles.pagination}>
        {cards.map((_, index) => (
          <div
            key={index}
            className={`${styles.dot} ${index === activeIndex ? styles.active : ""}`}
          />
        ))}
      </div>

      <div className={styles.swiperWrapper}>
        <div
          ref={scrollContainerRef}
          className={styles.scrollContainer}
          onScroll={handleScroll}
        >
          {/* Spacer for desktop centering */}
          <div className={styles.spacer} />

          {/* Cards with virtualization */}
          {cards.map((content, index) =>
            shouldRenderCard(index) ? (
              <Card
                key={content.title}
                title={content.title}
                description={content.description}
                youSave={content.youSave}
                image={content.image}
              />
            ) : (
              // Placeholder to maintain scroll position and snap points
              <div key={content.title} className={styles.card} />
            ),
          )}
        </div>

        {/* Navigation arrows */}
        <button
          className={`${styles.arrowButton} ${styles.arrowLeft} ${activeIndex === 0 ? styles.hidden : ""}`}
          onClick={handlePrevious}
          aria-label="Previous card"
        >
          <FaChevronLeft />
        </button>
        <button
          className={`${styles.arrowButton} ${styles.arrowRight} ${activeIndex === cards.length - 1 ? styles.hidden : ""}`}
          onClick={handleNext}
          aria-label="Next card"
        >
          <FaChevronRight />
        </button>
      </div>
    </>
  );
};

export default CardsSwiper;
